// Job roles — what a member says they do (`profiles.role`), NOT their
// platform permissions (`app_role`) or their team tag (lib/teamTags).
//
// ⚠️ This is the ONE client list. Onboarding, the profile editor, the admin
// user modal, UserContext's write allowlist and every byline read it; before
// 048 there were five copies, which is how a new role misses a screen. The
// database mirrors it in the `profiles_role_enum` CHECK (048, latest 057) —
// change both together.
//
// `dsp` decides whether verification displays as "Verified DSP" and whether
// the Verified DSP badge exists for this member (048's get_user_badges). A
// non-DSP member can still be verified — verification is also what lets
// anyone post — it just isn't labelled as DSP status.
export const ROLE_OPTIONS = [
  { id: 'professional',  label: 'Professional DSP',         emoji: '👔', desc: 'I work for an agency or organization', dsp: true },
  { id: 'self_directed', label: 'DSP in self-directed setting', emoji: '🤝', desc: 'I’m hired directly by the person or family I support', dsp: true },
  { id: 'family',        label: 'Family DSP',               emoji: '❤️', desc: 'I support a family member full-time',  dsp: true },
  { id: 'student',       label: 'New DSP',                  emoji: '📚', desc: 'I’ve just started working as a DSP',  dsp: true },
  { id: 'agency_staff',  label: 'Agency staff / leadership', emoji: '🏢', desc: 'I work at a provider agency, not in direct support', dsp: false },
  { id: 'family_member', label: 'Family member',            emoji: '🏡', desc: 'Someone I love receives support',       dsp: false },
  { id: 'advocate',      label: 'Advocate / educator',      emoji: '📣', desc: 'I advocate for or teach in the field',  dsp: false },
  { id: 'other',         label: 'Other',                    emoji: '✨', desc: 'Something else',                        dsp: false },
];

// Who verification is for, said the same way everywhere it is offered:
// onboarding's last step, VerifyModal and the feed composer. There is no
// external rule; this is the working criterion (2026-10). Self-attested, and
// every request is confirmed by an admin — agency email is off.
// `unlocks` lists what requireVerified() gates; keep it true to the code.
export const DSP_CRITERION = {
  headline: 'Verification is for DSPs',
  qualifies: 'You qualify if your main role at work is as a Direct Support Professional.',
  unlocks: 'Verified DSPs can post, reply, comment, mark posts helpful and share videos. Everyone can read and explore everything.',
  reviewed: 'A team member reviews every request.',
  notDsp: 'You can read and explore everything. Posting is for verified DSPs.',
};

export const VALID_ROLES = ROLE_OPTIONS.map((r) => r.id);

export const roleLabels = Object.fromEntries(ROLE_OPTIONS.map((r) => [r.id, r.label]));

/** Returns the display label for a role ID, or the raw value if unrecognized, or null if empty. */
export function getRoleLabel(roleId) {
  if (!roleId) return null;
  return roleLabels[roleId] || roleId;
}

/**
 * Whether a member's verification should read as DSP status. An unset role
 * counts as DSP: before 048 the only possible values were the three DSP roles
 * or NULL, so every existing verified member keeps their label.
 */
export function isDspRole(roleId) {
  if (!roleId) return true;
  const found = ROLE_OPTIONS.find((r) => r.id === roleId);
  return found ? found.dsp : true;
}

/**
 * Roles whose "agency" step asks where they live rather than where they work,
 * and whose answer never appears on a byline (056's shown_agency — change both
 * together). Self-directed DSPs are here because their employer is usually the
 * person they support, so a name typed there could identify them (057).
 */
export function isFamilyRole(roleId) {
  return roleId === 'family' || roleId === 'family_member' || roleId === 'self_directed';
}
